import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { clearAllTokens, getRefreshToken, setLocalToken, setRefreshToken } from '@/utils/auth';

interface ResponseData<T = any> {
  code: number;
  message: string;
  data: T;
}

export const API_BASE_URL = import.meta.env.VITE_API_BASE?.replace(/\/$/, '') || '/backend-api';

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 10 minutes to support large file uploads
});

let isRefreshing = false;
let pendingRequests: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function getResponseMessage(error: any): string | null {
  const message = error?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : null;
}

function onTokenRefreshed(newToken: string) {
  pendingRequests.forEach(({ resolve }) => resolve(newToken));
  pendingRequests = [];
}

function onTokenRefreshFailed(refreshError: unknown) {
  pendingRequests.forEach(({ reject }) => reject(refreshError));
  pendingRequests = [];
}

async function tryRefreshToken(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken)
    throw new Error('No refresh token');

  const res = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, { refresh: refreshToken });
  const newToken: string = res.data?.access ?? res.data?.data?.token;
  const newRefreshToken: string | null = res.data?.refresh ?? res.data?.data?.refreshToken ?? null;
  if (!newToken)
    throw new Error('Refresh failed');

  setLocalToken(newToken);
  if (newRefreshToken) {
    setRefreshToken(newRefreshToken);
  }
  return newToken;
}

service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    else if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json;charset=utf-8';
    }

    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

service.interceptors.response.use(
  (response: AxiosResponse<ResponseData>) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 403 && error.response?.data?.code === 'profile_setup_required') {
      if (window.location.pathname !== '/')
        window.location.replace('/?setup=1');
      return Promise.reject(error);
    }
    const responseMessage = getResponseMessage(error);
    if (responseMessage) {
      error.message = responseMessage;
    }

    const originalRequest = error.config;

    const isLocalPreviewSession = import.meta.env.DEV
      && import.meta.env.MODE !== 'local-acceptance'
      && ['probfun_local_teacher_preview', 'probfun_local_student_preview', 'probfun_local_admin_preview']
        .some(key => sessionStorage.getItem(key) === '1');

    if (
      error.response?.status === 401
      && !isLocalPreviewSession
      && originalRequest
      && !originalRequest._retried
      && !originalRequest.url?.includes('token/refresh')
    ) {
      originalRequest._retried = true;
      originalRequest.headers = originalRequest.headers ?? {};

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(service(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;
      try {
        const newToken = await tryRefreshToken();
        onTokenRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return service(originalRequest);
      }
      catch (refreshError) {
        onTokenRefreshFailed(refreshError);
        clearAllTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
      finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export async function get<T>(url: string, params?: any, config?: any): Promise<ResponseData<T>> {
  const response = await service.get(url, { ...config, params });
  if (response.status === 200) {
    if (response.data && (response.data.code === 200 || response.data.code === 0)) {
      return response.data;
    }
    if (typeof response.data === 'object' && !('code' in response.data)) {
      if ('status' in response.data && 'data' in response.data) {
        return { code: 0, message: response.data.status || 'success', data: response.data.data } as ResponseData<T>;
      }
      return { code: 0, message: 'success', data: response.data } as ResponseData<T>;
    }
    console.error('Request Error: Unexpected response format', { url, data: response.data });
  }
  throw new Error(response.data?.message || 'Request failed: invalid response format');
}

export async function post<T>(url: string, data?: any, config?: any): Promise<ResponseData<T>> {
  const response = await service.post(url, data, config);
  if (response.status >= 200 && response.status < 300) {
    if (response.data && (response.data.code === 200 || response.data.code === 0)) {
      return response.data;
    }
    if (typeof response.data === 'object' && !('code' in response.data)) {
      return { code: 0, message: 'success', data: response.data } as ResponseData<T>;
    }
    throw new Error(response.data?.message || 'Request failed');
  }
  else {
    throw new Error('Request failed');
  }
}

export async function postRaw<T>(url: string, data?: any, config?: any): Promise<T> {
  const response = await service.post(url, data, config);
  if (response.status >= 200 && response.status < 300) {
    return response.data;
  }
  throw new Error('Request failed');
}

export function getStoredAccessToken(): string | null {
  return localStorage.getItem('token');
}

export async function refreshAccessToken(): Promise<string> {
  return tryRefreshToken();
}

export async function del<T>(url: string, params?: any, config?: any): Promise<ResponseData<T>> {
  const response = await service.delete(url, { ...config, data: params });
  if (response.status >= 200 && response.status < 300) {
    if (response.data && (response.data.code === 200 || response.data.code === 0)) {
      return response.data;
    }
    if (typeof response.data === 'object' && response.data !== null && !('code' in response.data)) {
      return { code: 0, message: 'success', data: response.data } as ResponseData<T>;
    }
    return { code: 0, message: 'success', data: response.data as T };
  }
  throw new Error('Request failed');
}

export async function put<T>(url: string, data?: any, config?: any): Promise<ResponseData<T>> {
  const response = await service.put(url, data, config);
  if (response.status === 200 && (response.data.code === 200 || response.data.code === 0)) {
    return response.data;
  }
  else {
    throw new Error('Request failed');
  }
}
